import * as STD             from '@emersya/mpa-std';
import * as CM              from '@emersya/mpa-components-manager';

export interface IMenuPanelComponentConfiguration extends IMenuViewsComponentConfiguration {
	defaultSection?: CM.Component.TId;
};

export class MenuPanelComponent extends MenuViewsComponent<
	MenuPanelComponent,
	IMenuPanelComponentConfiguration
> {
	static override initialize() {
		MenuViewsComponent.initialize.call(this);
		this.declareEventType('menu-open');
		this.declareEventType('menu-close');
	};

	override async component_initialize() {
		await super.component_initialize();
		this.addEventListener('value', this.onInputButton.bind(this), {
			subType: 'input-button',
			listener: this
		});
	};

	protected override async component_onVisibilityUpdateBeforeChilds(isVisible: boolean) {
		if (isVisible && this.currentSections.length === 0) {
			await this.openDefaultMaybe();
		}
	};

	async openMaybe(childId?: string) {
		if (childId) {
			return (await this.open(childId));
		}

		return false;
	};

	override async open(childrenIds: string | string[], isAlreadyChecked: boolean = false) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		const closingChildrenIds = this.currentSections.filter(section => !childrenIds.includes(section.id)).
		map(section => section.id);

		if (closingChildrenIds.length > 0) {
			const closable = await this.close(closingChildrenIds);
			if (!closable) return false;
		}

		const proceed = await super.open(childrenIds, isAlreadyChecked);
		if (!proceed) return false;

		const alreadyOpenChildrenIds = this.currentSections.map(section => section.id);
		for (const childId of childrenIds) {
			if (alreadyOpenChildrenIds.includes(childId)) continue;

			const child = <
				SectionComponent | undefined
				>this.component_findOuter([childId]);

			if (!child) {
				this.component_throwUserError(`Child<${childId}> not found!`, {
					childId,
					component: this
				});
			}

			this.currentSections.push(child);
			child.component_DOM.classList.add('opened');
		}

		await this.component_dispatchVisibilityUpdateOnChilds();

		for (const child of this.currentSections) {
			await child.component_dispatchVisibilityUpdateOnChilds();
		}

		await this.dispatchEvent('menu-open', {
			details: {
				sections: this.currentSections
			}
		});

		return true;
	};

	async openDefaultMaybe() {
		if (this.component_configuration.defaultSection) {
			const defaultChild = this.component_find(`@.${this.component_configuration.defaultSection}`);
			if (!defaultChild.component_isDisabled) {
				await this.open(this.component_configuration.defaultSection);
			} else {
				const firstEnabledChild = this.component_outerChilds.find(x => !x.component_isDisabled);
				if (firstEnabledChild) {
					await this.open(firstEnabledChild.id);
				}
			}
		}
	};

	override async close(childrenIds: string | string[]) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		if (childrenIds.length === 0 || this.currentSections.length === 0) {
			return false;
		}

		const proceed = await super.close(childrenIds);
		if (!proceed) return false;

		const closedSections: SectionComponent[] = [];

		this.currentSections = this.currentSections.filter(section => {
			if (childrenIds.includes(section.id)) {
				closedSections.push(section);
				section.component_DOM.classList.remove('opened');
				return false;
			}
			return true;
		});

		await this.component_dispatchVisibilityUpdateOnChilds();

		for (const child of closedSections) {
			await child.component_dispatchVisibilityUpdateOnChilds();
		}

		for (const child of this.component_innerChilds) {
			if (child instanceof CM.DOMComponent) {
				await child.component_dispatchVisibilityUpdateOnChilds();
			}
		}

		await this.dispatchEvent('menu-close', {
			details: {
				newSections: this.currentSections,
				oldSections: closedSections
			}
		});

		return true;
	};

	private async openNext() {
		const currentSectionId = this.currentSections[0]!.id;
		const sectionIdx = this.component_outerChilds.findIndex(component => component.id === currentSectionId)!;

		if (sectionIdx < this.component_outerChilds.length - 1)
			await this.open(this.component_outerChilds[sectionIdx + 1]!.id);
	}

	private async openPrevious() {
		const currentSectionId = this.currentSections[0]!.id;
		const sectionIdx = this.component_outerChilds.findIndex(component => component.id === currentSectionId)!;
		// TODO First section of configurator menu (product-editor) is ignored
		if (sectionIdx > 1) {
			await this.open(this.component_outerChilds[sectionIdx - 1]!.id);
		}
	};

	private async onInputButton(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (!(event.target instanceof CM.Component)) {
			this.component_throwUserError('Target is not a component!', {event});
		}
		if (
			!event.target.component_parent ||
			(event.target.component_parent.component_parent !== this) ||
			(event.target.id !== 'header')
		) {
			return;
		}
		const section = event.target.component_parent;
		await this.open(section.id);
	};

	// protected override component_childIsVisible(
	// 	child: CM.DOMComponent<any, any, any>
	// ) {
	// 	if (!this.currentSection) {
	// 		return (child.component_isActive());
	// 	}
	// 	return (
	// 		(this.currentSection === child) &&
	// 		child.component_isActive()
	// 	);
	// };
	//
	// override component_childIsActive(child: CM.Component<any, any>) {
	// 	if (this.currentSection) {
	// 		if (this.currentSection !== child) {
	// 			return (false);
	// 		}
	// 	}
	// 	return (super.component_childIsActive(child));
	// };

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IMenuPanelComponentConfiguration
	) {
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
		void this.openNext;
		void this.openPrevious;
	};
};
export namespace NS {
	export const Component = MenuPanelComponent;
	export type Component = MenuPanelComponent;
	export type Configuration = IMenuPanelComponentConfiguration;
}
;
import './MenuPanel.scss';
import {
	MenuViewsComponent,
	IMenuViewsComponentConfiguration
}                           from '../MenuViews/MenuViews';
import { SectionComponent } from '../../Section/Section';
import manifestXMLSource    from './MenuPanel.xml';

CM.Manager.declare({
	id: 'menu-panel',
	type: MenuPanelComponent,
	manifest: manifestXMLSource
});
